import './FieldValidationPopup.scss';
import React, { useEffect, useState } from 'react';
import { Popup } from 'semantic-ui-react';
import { RUSTY_RED, SUNGLOW } from '../../../helpers/constants';
import { ErrorType, REQUIRED_FIELD_ERROR_MESSAGE } from '../../../templateeditor/components/FieldValidations/constants';
import { ErrorSettings } from '../../../types/template-types';
import { useAppSelector } from '../../../hooks/ReduxHooks';
import classNames from 'classnames';

type FieldValidationPopupProps = {
  className?: string;
  fieldId?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  errorSettings?: ErrorSettings;
  fieldType?: number;
  focused?: boolean;
  highlighted?: boolean;
  isMissingRequired?: boolean;
  isCompleted?: boolean;
  containsErrors?: boolean;
  showValidationPopup?: boolean;
};

const BASE_TRIANGLE_SIZE = 25;
const MIN_TRIANGLE_SIZE = 15;

export default function FieldValidationPopup(props: FieldValidationPopupProps) {
  const { fieldId, y, width, height, errorSettings, focused, highlighted, isMissingRequired, isCompleted, containsErrors, showValidationPopup } = props;

  const formEditorState = useAppSelector((state) => state.formeditor);
  const template = formEditorState.template;
  const page = template?.pages?.find((p) => p.fieldPositions?.some((fp) => fp.fieldId === fieldId))?.number;

  const errorColor = errorSettings?.errorType === ErrorType.Warning ? SUNGLOW : RUSTY_RED;
  const errorMessage = isMissingRequired ? REQUIRED_FIELD_ERROR_MESSAGE : errorSettings?.errorMessage || '';
  const borderColor = isMissingRequired ? RUSTY_RED : errorColor;

  // Calculate base size according to the smaller dimension to prevent stretching
  const baseSize = Math.min(width * 0.1, height * 0.1);
  const triangleSize = Math.max(MIN_TRIANGLE_SIZE, Math.min(BASE_TRIANGLE_SIZE, baseSize));

  // Use the same value for both width and height to maintain 1:1 aspect ratio
  const triangleWidth = triangleSize;
  const triangleHeight = triangleSize;

  // Top-right corner triangle points
  const polygonPoints = `${width},0 ${width - triangleWidth},0 ${width},${triangleHeight}`;

  // Determine if the popup should be shown below the field
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');

  useEffect(() => {
    // Calculate buffer based on error message length
    const baseBuffer = 130;
    const messageBuffer = errorMessage ? Math.min(errorMessage.length * 2, 50) : 0;
    const topBuffer = baseBuffer + messageBuffer;
    const isNearTop = y < topBuffer;

    // Special case: on first page, for fields near the top, show popup below the field
    if (isNearTop && page === 1) {
      setPlacement('bottom');
    } else {
      setPlacement('top');
    }
  }, [y]);

  // Don't render if it doesn't have errors or form is completed
  if (!containsErrors || isCompleted) {
    return null;
  }

  return (
    <Popup
      className={classNames('error-popup')}
      content={errorMessage}
      offset={placement === 'bottom' ? [12, height - 17] : [12, 0]}
      open={showValidationPopup === false ? false : focused || highlighted}
      pinned
      position={`${placement} right`}
      style={{
        borderLeftColor: borderColor
      }}
      trigger={<polygon fill={borderColor} fillOpacity={1} opacity={1} points={polygonPoints} />}
    />
  );
}
